(() => {
  if (customElements.get('custom-popup')) return;

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  class CustomPopup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.dialog = this.querySelector('[data-popup-dialog]');
      this.closeButtons = this.querySelectorAll('[data-popup-close]');
      this.previousActiveElement = null;
      this.autoTimer = null;
      this.consentObserver = null;
      this.hasOpened = false;
      this.autoOpenInitialized = false;

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          if (button.classList.contains('custom-popup__backdrop') && this.dataset.closeOnOverlay !== 'true') return;

          event.preventDefault();
          this.close();
        });
      });

      this.addEventListener('keydown', this.handleKeydown.bind(this));
      this.setupAutoOpenAfterConsentmo();
    }

    disconnectedCallback() {
      if (this.autoTimer) {
        window.clearTimeout(this.autoTimer);
      }

      if (this.consentObserver) {
        this.consentObserver.disconnect();
      }
    }

    setupAutoOpenAfterConsentmo() {
      if (this.isDesignMode()) return;

      const startAutoOpen = () => {
        if (this.autoOpenInitialized) return;

        this.autoOpenInitialized = true;

        if (this.consentObserver) {
          this.consentObserver.disconnect();
        }

        window.setTimeout(() => {
          this.setupAutoOpen();
        }, 3000);
      };

      document.addEventListener('consentmoSignal_onLoad', (event) => {
        const detail = event.detail || { isActive: false };

        if (!detail.isActive) {
          startAutoOpen();
          return;
        }

        document.addEventListener('consentmoSignal', () => {
          startAutoOpen();
        }, { once: true });
      }, { once: true });

      document.addEventListener('consentmoSignal', () => {
        startAutoOpen();
      }, { once: true });

      document.addEventListener('visitorConsentCollected', () => {
        startAutoOpen();
      }, { once: true });

      if (!this.hasVisibleConsentmoBanner()) {
        window.setTimeout(() => {
          if (!this.hasVisibleConsentmoBanner()) {
            startAutoOpen();
          }
        }, 1500);
      }

      this.consentObserver = new MutationObserver(() => {
        if (this.hasVisibleConsentmoBanner()) return;

        startAutoOpen();
      });

      this.consentObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden', 'aria-hidden']
      });

      window.setTimeout(() => {
        if (!this.hasVisibleConsentmoBanner()) {
          startAutoOpen();
        }
      }, 15000);
    }

    hasVisibleConsentmoBanner() {
      const selectors = [
        '#consentmo-gdpr-cookie-bar',
        '#consentmo-gdpr-preferences',
        '#consentmo-gdpr-cookie-bar-container',
        '.consentmo-gdpr-cookie-bar',
        '.consentmo-gdpr-preferences',
        '.consentmo-cookie-banner',
        '.consentmo-banner',
        '[id*="consentmo"]',
        '[class*="consentmo"]'
      ];

      return selectors.some((selector) => {
        return Array.from(document.querySelectorAll(selector)).some((element) => {
          return this.isElementVisible(element);
        });
      });
    }

    isElementVisible(element) {
      if (!element) return false;

      const style = window.getComputedStyle(element);

      if (style.display === 'none') return false;
      if (style.visibility === 'hidden') return false;
      if (style.opacity === '0') return false;
      if (element.hidden) return false;
      if (element.getAttribute('aria-hidden') === 'true') return false;

      const rect = element.getBoundingClientRect();

      return rect.width > 0 && rect.height > 0;
    }

    setupAutoOpen() {
      if (this.isDesignMode()) return;
      if (!this.shouldOpenAutomatically()) return;

      const openRule = this.dataset.openRule;

      if (openRule === 'manual') return;

      if (openRule === 'immediate') {
        this.autoTimer = window.setTimeout(() => this.open(), 80);
        return;
      }

      if (openRule === 'delay') {
        const delay = Number(this.dataset.delayMs || 0);
        this.autoTimer = window.setTimeout(() => this.open(), Math.max(delay, 0));
        return;
      }

      if (openRule === 'scroll') {
        const threshold = Number(this.dataset.scrollPercent || 50);

        const onScroll = () => {
          if (!this.shouldOpenAutomatically()) {
            window.removeEventListener('scroll', onScroll);
            return;
          }

          const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
          const scrollPercent = documentHeight <= 0 ? 100 : (window.scrollY / documentHeight) * 100;

          if (scrollPercent >= threshold) {
            window.removeEventListener('scroll', onScroll);
            this.open();
          }
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return;
      }

      if (openRule === 'exit_intent') {
        const supportsExitIntent = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

        if (!supportsExitIntent) return;

        const onMouseOut = (event) => {
          if (event.clientY > 0) return;
          if (event.relatedTarget !== null) return;

          document.removeEventListener('mouseout', onMouseOut);
          this.open();
        };

        document.addEventListener('mouseout', onMouseOut);
      }
    }

    shouldOpenAutomatically() {
      if (this.dataset.enabled !== 'true') return false;
      if (!this.matchesDeviceVisibility()) return false;
      if (!this.matchesPageRule()) return false;
      if (!this.matchesFrequency()) return false;

      return true;
    }

    matchesDeviceVisibility() {
      const visibility = this.dataset.visibility;

      if (visibility === 'desktop') {
        return window.matchMedia('(min-width: 750px)').matches;
      }

      if (visibility === 'mobile') {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      return true;
    }

    matchesPageRule() {
      const pageRule = this.dataset.pageRule || 'all';
      const currentPageType = this.dataset.currentPageType || '';

      if (pageRule === 'all') return true;

      return pageRule === currentPageType;
    }

    matchesFrequency() {
      const frequency = this.dataset.frequency || 'every_page_view';
      const storageKey = this.dataset.storageKey;

      if (!storageKey || frequency === 'every_page_view') return true;

      try {
        if (frequency === 'once_per_session') {
          return sessionStorage.getItem(storageKey) !== 'shown';
        }

        const storedValue = localStorage.getItem(storageKey);

        if (!storedValue) return true;

        if (frequency === 'once_forever') {
          return storedValue !== 'shown';
        }

        const lastShown = Number(storedValue);

        if (Number.isNaN(lastShown)) return true;

        const now = Date.now();
        const oneDay = 24 * 60 * 60 * 1000;

        if (frequency === 'once_per_day') {
          return now - lastShown > oneDay;
        }

        if (frequency === 'once_per_7_days') {
          return now - lastShown > 7 * oneDay;
        }
      } catch (error) {
        return true;
      }

      return true;
    }

    markAsShown() {
      const frequency = this.dataset.frequency || 'every_page_view';
      const storageKey = this.dataset.storageKey;

      if (!storageKey || frequency === 'every_page_view') return;

      try {
        if (frequency === 'once_per_session') {
          sessionStorage.setItem(storageKey, 'shown');
          return;
        }

        if (frequency === 'once_forever') {
          localStorage.setItem(storageKey, 'shown');
          return;
        }

        localStorage.setItem(storageKey, String(Date.now()));
      } catch (error) {
        // Storage can be blocked by privacy settings. In that case the popup still works.
      }
    }

    open(options = {}) {
      if (this.hasAttribute('data-state') && this.dataset.state === 'open') return;
      if (!options.ignoreRules && !this.isDesignMode() && !this.shouldOpenAutomatically()) return;

      this.previousActiveElement = document.activeElement;
      this.hidden = false;

      window.requestAnimationFrame(() => {
        this.dataset.state = 'open';
        this.hasOpened = true;

        if (this.dataset.lockScroll === 'true') {
          document.documentElement.classList.add('custom-popup-scroll-lock');
        }

        if (!options.preview) {
          this.markAsShown();
        }

        this.focusDialog();
        this.dispatchEvent(new CustomEvent('custom-popup:open', { bubbles: true }));
      });
    }

    close(options = {}) {
      if (this.hidden) return;

      this.dataset.state = 'closing';

      window.setTimeout(() => {
        this.hidden = true;
        this.removeAttribute('data-state');

        if (!document.querySelector('custom-popup[data-state="open"]')) {
          document.documentElement.classList.remove('custom-popup-scroll-lock');
        }

        if (!options.keepFocus && this.previousActiveElement && typeof this.previousActiveElement.focus === 'function') {
          this.previousActiveElement.focus({ preventScroll: true });
        }

        this.dispatchEvent(new CustomEvent('custom-popup:close', { bubbles: true }));
      }, this.getAnimationDuration());
    }

    focusDialog() {
      const firstFocusable = this.getFocusableElements()[0];

      if (firstFocusable) {
        firstFocusable.focus({ preventScroll: true });
        return;
      }

      this.dialog?.focus({ preventScroll: true });
    }

    getFocusableElements() {
      if (!this.dialog) return [];

      return Array.from(this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
        const style = window.getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden';
      });
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.dataset.closeOnEsc === 'true') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = this.getFocusableElements();

      if (focusableElements.length === 0) {
        event.preventDefault();
        this.dialog?.focus({ preventScroll: true });
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    getAnimationDuration() {
      const duration = Number(this.dataset.animationDuration || 250);

      return Number.isNaN(duration) ? 250 : Math.max(duration, 0);
    }

    isDesignMode() {
      return this.dataset.designMode === 'true' || window.Shopify?.designMode === true;
    }
  }

  customElements.define('custom-popup', CustomPopup);

  if (!window.__customPopupGlobalListeners) {
    window.__customPopupGlobalListeners = true;

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-popup-open], a[href^="#"]');

      if (!trigger) return;

      const targetId = trigger.getAttribute('data-popup-open') || trigger.getAttribute('href')?.replace('#', '');

      if (!targetId) return;

      const escapedTargetId = window.CSS?.escape ? CSS.escape(targetId) : targetId;
      const popup =
        document.getElementById(targetId) ||
        document.querySelector(`custom-popup[data-popup-id="${escapedTargetId}"]`);

      if (!popup || popup.tagName.toLowerCase() !== 'custom-popup') return;

      event.preventDefault();
      popup.open({ ignoreRules: true });
    });

    document.addEventListener('shopify:section:select', (event) => {
      const popup = event.target?.matches?.('custom-popup')
        ? event.target
        : event.target?.querySelector?.('custom-popup');

      if (!popup) return;

      popup.open({ ignoreRules: true, preview: true });
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      const popup = event.target?.matches?.('custom-popup')
        ? event.target
        : event.target?.querySelector?.('custom-popup');

      if (!popup) return;

      popup.close({ keepFocus: true });
    });

    document.addEventListener('shopify:block:select', (event) => {
      const popup =
        event.target?.closest?.('custom-popup') ||
        event.target?.querySelector?.('custom-popup');

      if (!popup) return;

      popup.open({ ignoreRules: true, preview: true });
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      const popup =
        event.target?.closest?.('custom-popup') ||
        event.target?.querySelector?.('custom-popup');

      if (!popup) return;

      popup.close({ keepFocus: true });
    });

    document.addEventListener('shopify:section:unload', (event) => {
      event.target.querySelectorAll?.('custom-popup').forEach((popup) => {
        popup.close({ keepFocus: true });
      });
    });
  }
})();